// src/pages/GroupCourseDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Users,
  Clock,
  Calendar,
  BookOpen,
  CheckCircle,
  TrendingUp,
  PlayCircle
} from 'lucide-react';
import { getCourseDetail, enrollInCourse } from '../services/groupCoursesApi';
import apiClient from '../services/apiClient';

const GroupCourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null); // 'pending_assignment' | 'assigned'
  const [assignedSectionId, setAssignedSectionId] = useState(null); // sectionId del grupo asignado
  
  useEffect(() => {
    loadCourseDetail();
  }, [courseId]);
  
  const loadCourseDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Cargar curso y estado de inscripción en paralelo
      const [courseResponse, statusResponse] = await Promise.allSettled([
        getCourseDetail(courseId),
        apiClient.get(`/group-courses/${courseId}/enrollment-status`)
      ]);

      if (courseResponse.status === 'fulfilled') {
        setCourse(courseResponse.value.course);
      } else {
        throw courseResponse.reason;
      }

      // Aplicar estado de inscripción persistente
      if (statusResponse.status === 'fulfilled') {
        const { status } = statusResponse.value.data;
        if (status === 'assigned') {
          setEnrollmentStatus('assigned');
          // Obtener sectionId del grupo asignado
          await loadAssignedSectionId();
        } else if (status === 'pending_assignment') {
          setEnrollmentStatus('pending_assignment');
        }
        // 'not_enrolled' → null → muestra "Inscribirme"
      }
    } catch (err) {
      console.error('Error cargando detalle del curso:', err);
      setError('No se pudo cargar el curso. Intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedSectionId = async () => {
    try {
      // Obtener grupos del estudiante
      const groupsResponse = await apiClient.get('/student-execution/my-groups');
      const allGroups = groupsResponse.data.data || [];
      
      // Buscar el grupo que corresponde a este curso
      const courseGroup = allGroups.find(group => {
        const groupCourseId = group.courseId || group.courseInfo?.courseId;
        return groupCourseId === courseId;
      });
      
      if (courseGroup) {
        // Extraer sectionId (puede venir como string o como objeto)
        const sectionId = typeof courseGroup.sectionId === 'string'
          ? courseGroup.sectionId
          : courseGroup.sectionId?.sectionId;
        
        console.log('✅ SectionId encontrado para navegación:', sectionId);
        setAssignedSectionId(sectionId);
        return sectionId; // Devolver para uso inmediato
      }
      return null;
    } catch (err) {
      console.error('Error obteniendo sectionId del grupo:', err);
      return null;
    }
  };

  const handleEnrollment = async () => {
    try {
      setEnrolling(true);
      setEnrollmentError(null);
      
      const response = await enrollInCourse(courseId);
      
      if (response.status === 'assigned') {
        // El docente ya le asignó un grupo → obtener sectionId y navegar al workspace
        const sectionId = await loadAssignedSectionId();
        if (sectionId) {
          navigate(`/course/${sectionId}/workspace`);
        } else {
          setEnrollmentError('No se pudo encontrar el grupo asignado. Recarga la página.');
        }
      } else {
        // status === 'pending_assignment' → mostrar mensaje de espera
        setEnrollmentStatus('pending_assignment');
      }
    } catch (err) {
      console.error('Error en inscripción:', err);
      setEnrollmentError(
        err.response?.data?.message || 
        err.response?.data?.error ||
        'No se pudo completar la inscripción. Intenta de nuevo más tarde.'
      );
    } finally {
      setEnrolling(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando curso...</p>
        </div>
      </div>
    );
  }
  
  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error al cargar el curso</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/cursos-grupales')}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }
  
  const DifficultyBadge = ({ difficulty }) => {
    const colors = {
      beginner: 'bg-green-100 text-green-800 border-green-200',
      intermediate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      advanced: 'bg-red-100 text-red-800 border-red-200'
    };
    
    const labels = {
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzado'
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colors[difficulty] || colors.beginner}`}>
        {labels[difficulty] || difficulty}
      </span>
    );
  };
  
  return (
    <div className="p-6 md:p-8 space-y-5 bg-gray-50">

      {/* ===== COURSE HEADER ===== */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">

          {/* Breadcrumb: Programas / {curso actual} */}
          <nav className="flex items-center gap-1.5 text-sm mb-3">
            <button
              onClick={() => navigate('/cursos-grupales')}
              className="text-gray-500 hover:text-gray-800 transition-colors"
            >
              Programas
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium">{course.title?.replace(/^Programa\s+/i, '')}</span>
          </nav>

          {/* Fila: Título + CTA */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
              <DifficultyBadge difficulty={course.difficulty} />
            </div>
            <div className="flex-shrink-0">
              {enrollmentStatus === 'assigned' ? (
                <button
                  onClick={() => assignedSectionId && navigate(`/course/${assignedSectionId}/workspace`)}
                  disabled={!assignedSectionId}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayCircle className="w-4 h-4" />
                  Ir a mi Workspace
                </button>
              ) : enrollmentStatus === 'pending_assignment' ? (
                <div className="px-4 py-2 bg-amber-50 text-amber-800 rounded-lg font-semibold text-sm border border-amber-200 flex items-center gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  Esperando asignación de grupo
                </div>
              ) : (
                <button
                  onClick={handleEnrollment}
                  disabled={enrolling}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {enrolling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Inscribiendo...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-4 h-4" />
                      Inscribirme
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Descripción */}
          <p className="text-gray-600 text-sm mb-3">{course.shortDescription}</p>

          {/* Metadata inline */}
          <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
            <Clock className="w-4 h-4" />
            <span>{course.estimatedHours || 0} horas</span>
            <span className="text-gray-300">•</span>
            <Calendar className="w-4 h-4" />
            <span>{course.weeks?.length || 18} semanas</span>
            <span className="text-gray-300">•</span>
            <Users className="w-4 h-4" />
            <span>Grupos {course.groupConfig?.minGroupSize || 3}–{course.groupConfig?.maxGroupSize || 8}</span>
          </div>

      </div>

      {/* ===== CONTENIDO PRINCIPAL ===== */}
      <div className="space-y-5">

        {/* ===== INFO GRID ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Columna izquierda: información del curso */}
          <div className="space-y-4">

            {/* Configuración grupal */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-blue-600" />
                Configuración grupal
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-50">
                  <span className="text-gray-500">Tamaño de grupo</span>
                  <span className="font-medium text-gray-800">
                    {course.groupConfig?.minGroupSize || 3}–{course.groupConfig?.maxGroupSize || 8}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-50">
                  <span className="text-gray-500">Peso entregable</span>
                  <span className="font-medium text-gray-800">{course.groupConfig?.deliverableWeight || 50}%</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-50">
                  <span className="text-gray-500">Feedback entre pares</span>
                  <span className="font-medium text-gray-800">{course.groupConfig?.crossFeedbackWeight || 30}%</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Coach IA</span>
                  <span className="font-medium text-gray-800">{course.groupConfig?.aiCoachWeight || 20}%</span>
                </div>
              </div>
            </div>

            {/* Habilidades */}
            {course.skills && course.skills.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  Habilidades que desarrollarás
                </h3>
                <div className="flex flex-wrap gap-2">
                  {course.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Columna derecha: estado del alumno */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-sm">
              <BookOpen className="w-4 h-4 text-blue-600" />
              Tu estado en el curso
            </h3>

            {enrollmentStatus === 'assigned' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-green-800 text-sm">Grupo asignado</p>
                    <p className="text-xs text-green-600">Tienes acceso completo al workspace</p>
                  </div>
                </div>
                <button
                  onClick={() => assignedSectionId && navigate(`/course/${assignedSectionId}/workspace`)}
                  disabled={!assignedSectionId}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayCircle className="w-5 h-5" />
                  Ir a mi Workspace
                </button>
              </div>
            ) : enrollmentStatus === 'pending_assignment' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Pre-inscripción registrada</p>
                    <p className="text-xs text-amber-600">El docente te asignará a un grupo pronto</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400">
                  Vuelve a esta página cuando tengas grupo asignado para acceder a tu workspace.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">Aún no estás inscrito en este curso.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Al inscribirte, el docente te asignará a un grupo de trabajo colaborativo.
                  </p>
                </div>
                {enrollmentError && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
                    {enrollmentError}
                  </div>
                )}
                <button
                  onClick={handleEnrollment}
                  disabled={enrolling}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {enrolling ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Inscribiendo...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-5 h-5" />
                      Inscribirme en el curso
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ===== CRONOGRAMA DEL CURSO ===== */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-blue-600" />
            Cronograma del curso
            <span className="ml-1 text-gray-400 font-normal">({course.weeks?.length || 0} semanas)</span>
          </h2>
          <div className="divide-y divide-gray-100">
            {course.weeks?.map((week) => (
              <div key={week.weekNumber} className="py-3 first:pt-0 last:pb-0">
                <p className="text-sm font-semibold text-gray-900">
                  Semana {week.weekNumber} — {week.title}
                </p>
                {week.learningObjectives && week.learningObjectives.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {week.learningObjectives.map((obj, idx) => (
                      <p key={idx} className="text-xs text-gray-500">
                        Objetivo {idx + 1}: {obj}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default GroupCourseDetailPage;
